/**
 * PublicComparisonLinkPage.jsx : page du lien de comparaison, sans compte (D257).
 *
 * Adresse `/comparaison-partagee#<jeton>` : le jeton reste dans le fragment,
 * gardé en mémoire et envoyé en en-tête aux routes publiques. Montée avant
 * <App /> (index.js) : aucun appel authentifié, aucun menu de l'application.
 *
 * Contenu : CompareSessionsView, la même vue que la comparaison du
 * propriétaire, sur fond clair : onglets et alignement libres, barre de plage
 * absente (le serveur ne renvoie que les mesures de la période du lien), PDF
 * avec la mention « partagée par lien », jamais de bouton de partage ni de retour.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTheme, TYPE } from '../../constants/theme';
import CompareSessionsView from '../CompareSessionsView';
import { RANGES } from '../analysisRange';
import { usePublicComparisonLink } from './usePublicComparisonLink';
import { useHashToken, useNoIndex } from './usePublicLinkPage';
import { formatLinkDate, periodRangeText } from './linkText';
import { PublicLinkError, PublicTopBar } from './_publicBlocks';

/** Vue de la comparaison partagée, montée une fois le lien chargé. */
function SharedComparison({ payload, loaded, theme }) {
  const { t } = useTranslation();
  // Mode d'axe proposé par le créateur ; toute l'étendue reçue (déjà bornée à la période).
  const [range, setRange] = useState(() => (payload.align ? { align: payload.align, range: RANGES.ALL } : null));
  const validUntil = formatLinkDate(payload.expiresAt);
  const scope = payload.period
    ? t('comparisonLink.scope.period', {
      range: periodRangeText(new Date(payload.period.from).getTime(), new Date(payload.period.to).getTime()),
    })
    : t('comparisonLink.scope.whole');
  return (
    <>
      <CompareSessionsView loaded={loaded} darkMode={false} variant="owner" range={range} onRangeChange={setRange}
        rangeLocked pdfMention={t('comparisonLink.public.pdfMention', { date: validUntil })}
        notice={<span data-testid="public-comparison-notice">
          {t('comparisonLink.public.badge')}{' · '}{scope}{' · '}{t('comparisonLink.public.validUntil', { date: validUntil })}
        </span>} />
      <p style={{ ...TYPE.body, fontSize: 12, maxWidth: 1000, margin: '0 auto', color: theme.textSecondary }}>
        {t('comparisonLink.public.notShared')}
      </p>
    </>
  );
}

export default function PublicComparisonLinkPage() {
  const { t } = useTranslation();
  const theme = getTheme(false);
  useNoIndex();
  const token = useHashToken();
  const { status, errorCode, payload, loaded } = usePublicComparisonLink(token);

  const content = () => {
    if (status === 'loading') {
      return <p role="status" style={{ ...TYPE.body, textAlign: 'center', padding: 40, color: theme.textSecondary }}>{t('comparisonLink.public.loading')}</p>;
    }
    if (status === 'error') return <PublicLinkError code={errorCode} theme={theme} />;
    return <SharedComparison key={payload.expiresAt} payload={payload} loaded={loaded} theme={theme} />;
  };

  return (
    <div data-testid="public-comparison-link-page" style={{ minHeight: '100vh', background: theme.bg }}>
      <PublicTopBar />
      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>{content()}</main>
    </div>
  );
}
